import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import type { ViewId } from "@storytell/types";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbStack, TbX } from "solid-icons/tb";
import { Show, onCleanup, onMount } from "solid-js";
import { twMerge } from "tailwind-merge";
import { useWire } from "~/wire";

export const VariableV1View = (props: {
  notInPromptBar?: boolean;
  id: string;
  selected?: boolean;
  onDelete: () => void;
  isEditable?: boolean;
  text?: string;
}) => {
  const wire = useWire();
  const view = () => wire.views.get(props.id as ViewId);

  // TODO: @andi, Better way to communicate between prompt bar and app state?
  onMount(() => {
    if (props.notInPromptBar) return;
    const unsubscribe = subscribeToGlobalEvent("removed-item-from-prompt-scope", (event) => {
      if (event.detail.id === props.id) {
        props.onDelete();
      }
    });
    dispatchGlobalEvent("added-mention-to-prompt-bar", {
      id: props.id,
    });
    onCleanup(() => {
      unsubscribe();
      dispatchGlobalEvent("removed-mention-from-prompt-bar", {
        id: props.id,
      });
    });
  });

  const truncatedName = () => {
    const maxLength = 40;
    const name = view()?.name || props.text || "Unknown";
    if (name.length <= maxLength) return name;

    const extIndex = name.lastIndexOf(".");
    const extension = extIndex !== -1 ? name.slice(extIndex) : "";
    const nameWithoutExt = extIndex !== -1 ? name.slice(0, extIndex) : name;

    const charsToShow = maxLength - extension.length - 3; // 3 for "..."
    if (charsToShow <= 0) return name;

    const frontChars = Math.ceil(charsToShow / 2);
    const backChars = Math.floor(charsToShow / 2);

    return `${nameWithoutExt.slice(0, frontChars)}...${nameWithoutExt.slice(nameWithoutExt.length - backChars)}${extension}`;
  };

  const viewName = view()?.name || props.text || "Unknown Collection";

  return (
    <span
      onClick={(e) => {
        // Don't navigate if clicking the delete button
        if ((e.target as HTMLElement).closest("button")) return;
        e.preventDefault();
        // Clear any selected concept first to ensure we show the assets table
        dispatchGlobalEvent("clear-selected-concept", undefined);
        // Set the tab first, then the view to ensure correct order
        // Both methods navigate, which opens the pane if it's not open
        wire.knowledgeUi.setActiveTab("assets");
        // Use a small delay to ensure setActiveTab completes before setActiveView
        setTimeout(() => {
          wire.knowledgeUi.setActiveView(props.id as ViewId);
        }, 0);
      }}
      class={twMerge(
        "group/mention inline-flex items-center gap-1 rounded-lg bg-tile hover:bg-tile-hover text-sm text-primary-text border border-primary-border px-1 cursor-pointer",
        props.selected && "ring-2 ring-brand",
      )}
    >
      <TbStack class="size-3.5 group-hover/mention:hidden" />
      <Show when={props.isEditable}>
        <StTooltip placement="top" contentProps={{ class: "z-20" }} content={<p>Remove</p>}>
          <StTooltipTrigger
            as="button"
            onClick={() => {
              props.onDelete();
            }}
            type="button"
            class="transition-transform text-concept-icon cursor-pointer hidden group-hover/mention:flex items-center gap-1"
          >
            <TbX class="size-3.5 text-secondary-text hover:[stroke-width:3] hover:drop-shadow-sm" />
            <span class="text-secondary-text pointer-events-none cursor-default"> | </span>
          </StTooltipTrigger>
        </StTooltip>
      </Show>
      <span class="hover:font-bold">{truncatedName()}</span>
    </span>
  );
};
